import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Edit3, Trash2, Globe, X, Building, FileText, FolderKanban, Mail, CreditCard, Upload } from "lucide-react";
import { SubscriptionModal } from "@/pages/organizations/SubscriptionModal";

const CURRENCIES = ["USD", "INR", "EUR", "GBP"];

export default function Organizations() {
  const [orgs, setOrgs] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [subOrg, setSubOrg] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/organizations");
      setOrgs(data || []);
    } catch { toast.error("Could not load organizations"); }
  };

  useEffect(() => { load(); }, []);

  const remove = async (o) => {
    if (o.is_default) { toast.error("Cannot delete the default organization"); return; }
    if (!confirm(`Delete "${o.name}"? Existing rows will be reassigned to the default org.`)) return;
    try {
      await api.delete(`/organizations/${o.id}`);
      toast.success("Deleted — rows reassigned to default org");
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-mono">MULTI-TENANT ROOT</div>
          <h1 className="font-heading text-4xl font-black tracking-tight">Organizations</h1>
          <p className="text-sm text-zinc-600 mt-1 font-mono">
            Each org has its own clients, jobs, invoices, email accounts &amp; senders. Attendance + Payroll are shared across all orgs.
          </p>
        </div>
        <button
          onClick={() => { setEditing(null); setShowForm(true); }}
          className="brut-btn px-4 py-2 text-sm flex items-center gap-2"
          data-testid="new-org-btn"
        >
          <Plus className="w-4 h-4" /> NEW ORGANIZATION
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {orgs.length === 0 && (
          <div className="brut-card p-8 text-center text-zinc-400 font-mono text-xs col-span-full">
            NO ORGANIZATIONS YET — CREATE ONE TO BEGIN
          </div>
        )}
        {orgs.map((o) => (
          <div key={o.id} className="brut-card p-4 space-y-3" data-testid={`org-card-${o.id}`}>
            <div className="flex items-start justify-between">
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-7 h-7 border border-zinc-900 bg-yellow-200 flex items-center justify-center">
                    <Building className="w-3.5 h-3.5" />
                  </div>
                  {o.is_default && (
                    <span className="text-[10px] font-mono font-bold bg-zinc-900 text-white px-1.5 py-0.5">DEFAULT</span>
                  )}
                </div>
                <div className="font-heading text-lg font-black leading-tight" data-testid={`org-name-${o.id}`}>{o.name}</div>
                {o.short_code && (
                  <div className="text-xs font-mono text-zinc-500 mt-0.5">{o.short_code} · {o.default_currency}</div>
                )}
                {o.address && <div className="text-xs text-zinc-600 mt-1">{o.address}</div>}
              </div>
              <div className="flex gap-1 shrink-0">
                <button
                  onClick={() => setSubOrg(o)}
                  className="brut-btn-secondary p-1.5 bg-[#FBF3DE] hover:bg-[#F5E7BE]"
                  title="Subscription / Plan"
                  data-testid={`org-plan-${o.id}`}
                >
                  <CreditCard className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => { setEditing(o); setShowForm(true); }}
                  className="brut-btn-secondary p-1.5"
                  title="Edit"
                  data-testid={`edit-org-${o.id}`}
                >
                  <Edit3 className="w-3.5 h-3.5" />
                </button>
                {!o.is_default && (
                  <button
                    onClick={() => remove(o)}
                    className="text-red-600 hover:bg-red-50 p-1.5 border border-red-300"
                    title="Delete"
                    data-testid={`delete-org-${o.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2 pt-2 border-t border-zinc-200">
              <Counter icon={FolderKanban} label="Jobs" value={o.counts?.jobs ?? 0} />
              <Counter icon={Building} label="Clients" value={o.counts?.clients ?? 0} />
              <Counter icon={FileText} label="Invoices" value={o.counts?.invoices ?? 0} />
              <Counter icon={Mail} label="Email Accounts" value={(o.counts?.email_accounts ?? 0) + (o.counts?.invoice_senders ?? 0)} />
            </div>

            {(o.gstin || o.pan) && (
              <div className="flex flex-wrap gap-2 pt-2 border-t border-zinc-200 text-[10px] font-mono">
                {o.gstin && <span className="border border-zinc-900 px-2 py-0.5">GSTIN {o.gstin}</span>}
                {o.pan && <span className="border border-zinc-900 px-2 py-0.5">PAN {o.pan}</span>}
              </div>
            )}

            {(o.subscription || o.legal_name) && (
              <div className="flex flex-wrap gap-1.5 pt-2 border-t border-zinc-200 text-[10px] font-mono" data-testid={`org-badges-${o.id}`}>
                {o.subscription && (
                  <span className={`border px-1.5 py-0.5 font-bold ${o.subscription.status === "active" ? "border-[#1F5F3F] bg-[#EAF3EE] text-[#1F5F3F]" : "border-zinc-400 bg-zinc-100 text-zinc-500"}`}>
                    {(o.subscription.plan_name || "PLAN").toUpperCase()} · {(o.subscription.status || "").toUpperCase()}
                  </span>
                )}
                {o.legal_name && <span className="border border-[#B8860B] bg-[#FBF3DE] px-1.5 py-0.5 text-[#8B6508] font-bold">CUSTOM LETTERHEAD</span>}
              </div>
            )}

            {o.settings && (
              <div className="flex flex-wrap gap-1.5 pt-2 border-t border-zinc-200 text-[10px] font-mono" data-testid={`org-settings-${o.id}`}>
                <span className={`border border-zinc-900 px-1.5 py-0.5 ${o.settings.pdf_watermark_enabled ? "bg-yellow-100" : "bg-zinc-100 text-zinc-500"}`}>
                  WATERMARK · {o.settings.pdf_watermark_enabled ? "ON" : "OFF"}
                </span>
                <span className="border border-zinc-900 px-1.5 py-0.5 bg-orange-50" title="Stale-job thresholds (days)">
                  STALE · {o.settings.stale_days_urgent}/{o.settings.stale_days_high}/{o.settings.stale_days_normal}/{o.settings.stale_days_low}d
                </span>
              </div>
            )}
          </div>
        ))}
      </div>

      {showForm && (
        <OrgForm
          org={editing}
          onClose={() => setShowForm(false)}
          onSaved={async () => { await load(); setShowForm(false); }}
        />
      )}
      {subOrg && <SubscriptionModal org={subOrg} onClose={() => setSubOrg(null)} />}
    </div>
  );
}

function Counter({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-2 text-xs font-mono">
      <Icon className="w-3.5 h-3.5 text-zinc-500" />
      <span className="text-zinc-500">{label}:</span>
      <b>{value}</b>
    </div>
  );
}

function OrgForm({ org, onClose, onSaved }) {
  const isEdit = Boolean(org?.id);
  const [name, setName] = useState(org?.name || "");
  const [shortCode, setShortCode] = useState(org?.short_code || "");
  const [address, setAddress] = useState(org?.address || "");
  const [gstin, setGstin] = useState(org?.gstin || "");
  const [pan, setPan] = useState(org?.pan || "");
  const [currency, setCurrency] = useState(org?.default_currency || "USD");
  // Company profile (letterhead identity)
  const P = (k) => org?.[k] || "";
  const [profile, setProfile] = useState({
    legal_name: P("legal_name"), brand_name: P("brand_name"), brand_suffix: P("brand_suffix"),
    tagline: P("tagline"), sub_tagline: P("sub_tagline"), headline: P("headline"),
    reg_address: P("reg_address"), phones: P("phones"), emails: P("emails"), website: P("website"),
    cin: P("cin"), reg_no: P("reg_no"), udyam: P("udyam"), tan: P("tan"),
    director_name: P("director_name"), director_title: P("director_title"),
    hr_manager_name: P("hr_manager_name"), hr_manager_title: P("hr_manager_title"),
    bank_name: P("bank_name"), bank_account: P("bank_account"), bank_ifsc: P("bank_ifsc"),
    bank_branch: P("bank_branch"), upi_id: P("upi_id"),
  });
  const setP = (k, v) => setProfile((s) => ({ ...s, [k]: v }));
  const [logoB64, setLogoB64] = useState(undefined);   // undefined = untouched
  const [stampB64, setStampB64] = useState(undefined);
  // Org admin account (create only)
  const [adminName, setAdminName] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const [adminPassword, setAdminPassword] = useState("");
  // Settings — defaults come from the API on edit; new orgs inherit the global defaults.
  const initS = org?.settings || {};
  const [watermark, setWatermark] = useState(initS.pdf_watermark_enabled ?? true);
  const [staleUrgent, setStaleUrgent] = useState(initS.stale_days_urgent ?? 1);
  const [staleHigh, setStaleHigh] = useState(initS.stale_days_high ?? 2);
  const [staleNormal, setStaleNormal] = useState(initS.stale_days_normal ?? 5);
  const [staleLow, setStaleLow] = useState(initS.stale_days_low ?? 10);
  const [saving, setSaving] = useState(false);

  const readImage = (file, setter) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Image too large (max 2MB)"); return; }
    const r = new FileReader();
    r.onload = () => setter(r.result);
    r.readAsDataURL(file);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Name is required"); return; }
    if (!isEdit && adminEmail.trim() && adminPassword.length < 8) {
      toast.error("Org admin password must be at least 8 characters"); return;
    }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(), short_code: shortCode.trim(), address: address.trim(),
        gstin: gstin.trim(), pan: pan.trim(), default_currency: currency,
        pdf_watermark_enabled: !!watermark,
        stale_days_urgent: Number(staleUrgent) || 0,
        stale_days_high: Number(staleHigh) || 0,
        stale_days_normal: Number(staleNormal) || 0,
        stale_days_low: Number(staleLow) || 0,
        ...profile,
      };
      if (logoB64 !== undefined) payload.logo_b64 = logoB64;
      if (stampB64 !== undefined) payload.stamp_b64 = stampB64;
      if (!isEdit && adminEmail.trim()) {
        payload.admin_name = adminName.trim();
        payload.admin_email = adminEmail.trim();
        payload.admin_password = adminPassword;
      }
      if (isEdit) await api.patch(`/organizations/${org.id}`, payload);
      else await api.post("/organizations", payload);
      toast.success(isEdit ? "Organization updated" : "Organization created");
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
    setSaving(false);
  };

  const pInput = (key, label, ph = "", mono = false) => (
    <Field label={label}>
      <input value={profile[key]} onChange={(e) => setP(key, e.target.value)} placeholder={ph}
        className={`brut-input w-full px-3 py-2 text-sm ${mono ? "font-mono" : ""}`} data-testid={`org-profile-${key}`} />
    </Field>
  );

  return (
    <div className="fixed inset-0 bg-zinc-900/40 z-50 flex items-center justify-center p-2 sm:p-4" onClick={onClose}>
      <form onSubmit={save} className="brut-card max-w-2xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between px-6 pt-6 pb-3 border-b border-zinc-200 shrink-0">
          <div>
            <div className="label-mono">ORGANIZATION</div>
            <h2 className="font-heading text-2xl font-black">{isEdit ? "Edit organization" : "New organization"}</h2>
          </div>
          <button type="button" onClick={onClose} className="brut-btn-secondary p-1.5"><X className="w-4 h-4" /></button>
        </div>
        <div className="px-6 py-4 space-y-4 overflow-y-auto flex-1">
          <Field label="ORGANIZATION NAME *">
            <input required value={name} onChange={(e) => setName(e.target.value)} placeholder="LAND DRAFTERS INDIA PRIVATE LIMITED, PUNE" className="brut-input w-full px-3 py-2 text-sm" data-testid="org-name-input" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="SHORT CODE">
              <input value={shortCode} onChange={(e) => setShortCode(e.target.value)} placeholder="LD-IN-PUNE" className="brut-input w-full px-3 py-2 text-sm font-mono" data-testid="org-shortcode-input" />
            </Field>
            <Field label="DEFAULT CURRENCY">
              <select value={currency} onChange={(e) => setCurrency(e.target.value)} className="brut-input w-full px-3 py-2 text-sm font-mono" data-testid="org-currency-select">
                {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
          </div>
          <Field label="ADDRESS">
            <input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Pune, Maharashtra, India" className="brut-input w-full px-3 py-2 text-sm" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="GSTIN">
              <input value={gstin} onChange={(e) => setGstin(e.target.value)} className="brut-input w-full px-3 py-2 text-sm font-mono" />
            </Field>
            <Field label="PAN">
              <input value={pan} onChange={(e) => setPan(e.target.value)} className="brut-input w-full px-3 py-2 text-sm font-mono" />
            </Field>
          </div>

          {/* ── Company Profile (letterhead & documents) ─────────────────── */}
          <div className="border-t border-zinc-300 pt-3 space-y-3">
            <div className="label-mono text-zinc-500">COMPANY PROFILE — LETTERHEAD &amp; DOCUMENTS</div>
            <p className="text-xs text-zinc-500">
              Fill these and every HR letter, payslip and PDF generated for this org's employees carries THIS company's identity instead of the default Land Drafters branding. Leave blank to keep the default.
            </p>
            {pInput("legal_name", "LEGAL NAME (as registered)", "Acme Geospatial Private Limited")}
            <div className="grid grid-cols-2 gap-3">
              {pInput("brand_name", "BRAND NAME (big, green)", "ACME GEO")}
              {pInput("brand_suffix", "NAME SUFFIX (gold)", "INDIA PVT. LTD.")}
            </div>
            {pInput("tagline", "TAGLINE (line 1)", "CIVIL ENGINEERING | LAND SURVEYING")}
            {pInput("sub_tagline", "SUB-TAGLINE (line 2)", "Practical Engineering Solutions")}
            {pInput("headline", "GOLD HEADLINE (italic)", "Global Geospatial Solutions Across All Seven Continents")}
            <Field label="REGISTERED ADDRESS (one line per row, max 4)">
              <textarea rows={3} value={profile.reg_address} onChange={(e) => setP("reg_address", e.target.value)}
                placeholder={"Office No. 1, Business Hub,\nMain Road, City 411001,\nMaharashtra INDIA"}
                className="brut-input w-full px-3 py-2 text-sm" data-testid="org-profile-reg_address" />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              {pInput("phones", "PHONES", "+91 98765 43210 | +91 91234 56789", true)}
              {pInput("emails", "EMAILS", "hr@acme.com", true)}
            </div>
            {pInput("website", "WEBSITE", "www.acme.com", true)}
            <div className="grid grid-cols-2 gap-3">
              {pInput("cin", "CIN", "U74999MH2021PTC000000", true)}
              {pInput("reg_no", "REGISTRATION NO.", "", true)}
            </div>
            <div className="grid grid-cols-2 gap-3">
              {pInput("udyam", "UDYAM / MSME NO. (optional)", "", true)}
              {pInput("tan", "TAN (optional)", "", true)}
            </div>
            <div className="grid grid-cols-2 gap-3">
              {pInput("director_name", "DIRECTOR NAME", "Mr. Rakesh Sharma")}
              {pInput("director_title", "DIRECTOR TITLE", "CEO / Director")}
            </div>
            <div className="grid grid-cols-2 gap-3">
              {pInput("hr_manager_name", "HR SIGNATORY NAME", "Mrs. Priya Sharma")}
              {pInput("hr_manager_title", "HR SIGNATORY TITLE", "Director / HR Manager")}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label={`COMPANY LOGO ${org?.has_logo || logoB64 ? "· UPLOADED ✓" : ""}`}>
                <label className="brut-btn-secondary px-3 py-2 text-xs flex items-center gap-2 cursor-pointer w-fit">
                  <Upload className="w-3.5 h-3.5" /> {logoB64 ? "REPLACE LOGO" : "UPLOAD LOGO"}
                  <input type="file" accept="image/*" className="hidden" data-testid="org-logo-upload"
                    onChange={(e) => readImage(e.target.files?.[0], setLogoB64)} />
                </label>
              </Field>
              <Field label={`COMPANY STAMP ${org?.has_stamp || stampB64 ? "· UPLOADED ✓" : ""}`}>
                <label className="brut-btn-secondary px-3 py-2 text-xs flex items-center gap-2 cursor-pointer w-fit">
                  <Upload className="w-3.5 h-3.5" /> {stampB64 ? "REPLACE STAMP" : "UPLOAD STAMP"}
                  <input type="file" accept="image/*" className="hidden" data-testid="org-stamp-upload"
                    onChange={(e) => readImage(e.target.files?.[0], setStampB64)} />
                </label>
              </Field>
            </div>
            <div className="label-mono text-zinc-500 pt-1">BANKING (invoices)</div>
            <div className="grid grid-cols-2 gap-3">
              {pInput("bank_name", "BANK NAME", "HDFC Bank")}
              {pInput("bank_account", "ACCOUNT NO.", "", true)}
            </div>
            <div className="grid grid-cols-2 gap-3">
              {pInput("bank_ifsc", "IFSC", "", true)}
              {pInput("bank_branch", "BRANCH", "")}
            </div>
            {pInput("upi_id", "UPI ID (optional)", "acme@hdfcbank", true)}
          </div>

          {/* ── Org Admin account (create only) ──────────────────────────── */}
          {!isEdit && (
            <div className="border-t border-zinc-300 pt-3 space-y-3">
              <div className="label-mono text-zinc-500">ORG ADMIN ACCOUNT (OPTIONAL)</div>
              <p className="text-xs text-zinc-500">
                Create a login for this company so they can manage their own data. Leave blank to skip.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <Field label="ADMIN NAME">
                  <input value={adminName} onChange={(e) => setAdminName(e.target.value)} placeholder="Rakesh Sharma" className="brut-input w-full px-3 py-2 text-sm" data-testid="org-admin-name" />
                </Field>
                <Field label="ADMIN EMAIL">
                  <input type="email" value={adminEmail} onChange={(e) => setAdminEmail(e.target.value)} placeholder="admin@acme.com" className="brut-input w-full px-3 py-2 text-sm font-mono" data-testid="org-admin-email" />
                </Field>
              </div>
              <Field label="ADMIN PASSWORD (min 8 chars)">
                <input type="password" value={adminPassword} onChange={(e) => setAdminPassword(e.target.value)} className="brut-input w-full px-3 py-2 text-sm font-mono" data-testid="org-admin-password" />
              </Field>
            </div>
          )}

          {/* ── Per-org settings ───────────────────────────────────────────── */}
          <div className="border-t border-zinc-300 pt-3 space-y-3">
            <div className="label-mono text-zinc-500">PER-ORG SETTINGS</div>

            <label className="flex items-start gap-2 cursor-pointer" data-testid="org-watermark-toggle">
              <input
                type="checkbox"
                checked={watermark}
                onChange={(e) => setWatermark(e.target.checked)}
                className="mt-1"
              />
              <span>
                <div className="font-mono text-sm font-bold">PDF watermark</div>
                <div className="text-xs text-zinc-500">
                  Stamp every invoice + payslip PDF with a diagonal anti-leak watermark (client initials / employee code + secondary line). Turn off if a customer doesn't want it on printed deliverables.
                </div>
              </span>
            </label>

            <div>
              <div className="font-mono text-sm font-bold mb-1">Stale-job thresholds (days)</div>
              <div className="text-xs text-zinc-500 mb-2">
                Flag jobs sitting in the same stage longer than this. Lower = pushier alerts.
              </div>
              <div className="grid grid-cols-4 gap-2">
                <Field label="URGENT">
                  <input type="number" min={0} max={365} value={staleUrgent}
                    onChange={(e) => setStaleUrgent(e.target.value)}
                    className="brut-input w-full px-3 py-1.5 text-sm font-mono text-center"
                    data-testid="org-stale-urgent" />
                </Field>
                <Field label="HIGH">
                  <input type="number" min={0} max={365} value={staleHigh}
                    onChange={(e) => setStaleHigh(e.target.value)}
                    className="brut-input w-full px-3 py-1.5 text-sm font-mono text-center"
                    data-testid="org-stale-high" />
                </Field>
                <Field label="NORMAL">
                  <input type="number" min={0} max={365} value={staleNormal}
                    onChange={(e) => setStaleNormal(e.target.value)}
                    className="brut-input w-full px-3 py-1.5 text-sm font-mono text-center"
                    data-testid="org-stale-normal" />
                </Field>
                <Field label="LOW">
                  <input type="number" min={0} max={365} value={staleLow}
                    onChange={(e) => setStaleLow(e.target.value)}
                    className="brut-input w-full px-3 py-1.5 text-sm font-mono text-center"
                    data-testid="org-stale-low" />
                </Field>
              </div>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 px-6 py-3 border-t border-zinc-300 shrink-0">
          <button type="button" onClick={onClose} className="brut-btn-secondary px-4 py-2 text-sm">CANCEL</button>
          <button type="submit" disabled={saving} className="brut-btn px-4 py-2 text-sm disabled:opacity-60" data-testid="save-org-btn">
            <Globe className="w-4 h-4 inline-block mr-1" />
            {saving ? "SAVING…" : (isEdit ? "UPDATE" : "CREATE")}
          </button>
        </div>
      </form>
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <div className="label-mono mb-1">{label}</div>
    {children}
  </label>
);
